import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const fmt2 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/analytics/summary?days=${days}`).then(r => setData(r.data)); }, [days]);

  if (!data) return <div data-testid="analytics-page">Yükleniyor…</div>;

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-caps">Ciro · Kâr · Trend</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Analitik</h1>
        </div>
        <div className="flex gap-2">
          {[7,30,90].map(d => (
            <button key={d} onClick={()=>setDays(d)} className={`px-4 py-2 border text-xs uppercase tracking-widest ${days===d?"border-[--gold] text-[--gold]":"border-white/10 text-neutral-400"}`} style={days===d?{borderColor:"var(--gold)",color:"var(--gold)"}:{}} data-testid={`period-${d}`}>{d} Gün</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
        <div className="card-flat p-6"><div className="label-caps">Toplam Satış</div><div className="font-mono text-2xl mt-3 tabular-nums" style={{color:"var(--gold)"}}>{fmt2(data.total_sale)} ₺</div></div>
        <div className="card-flat p-6"><div className="label-caps">Hurda Alım</div><div className="font-mono text-2xl mt-3 tabular-nums">{fmt2(data.total_scrap)} ₺</div></div>
        <div className="card-flat p-6"><div className="label-caps">İşlem Sayısı</div><div className="font-mono text-2xl mt-3 tabular-nums">{data.total_txns}</div></div>
        <div className="card-flat p-6"><div className="label-caps">Günlük Ort.</div><div className="font-mono text-2xl mt-3 tabular-nums">{fmt2(data.total_sale/Math.max(1,data.daily.length))} ₺</div></div>
      </div>

      <div className="card-flat p-6">
        <div className="label-caps mb-2">Günlük Satış Grafiği</div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.daily} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#666", fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)" }} labelStyle={{ color: "#999" }} formatter={(v)=>[fmt2(v)+" ₺"]} />
              <Line type="monotone" dataKey="sale" stroke="#D4AF37" strokeWidth={1.5} dot={{r:2}} />
              <Line type="monotone" dataKey="scrap" stroke="#10B981" strokeWidth={1} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat p-6">
          <div className="label-caps mb-2">En Çok Satan 10 Ürün</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.top_products.slice(0,10)} layout="vertical" margin={{ top: 10, right: 20, left: 100, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#666", fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#999", fontSize: 10 }} width={100} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)" }} formatter={(v)=>[fmt2(v)+" ₺"]} />
                <Bar dataKey="revenue" fill="#D4AF37" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-6">
          <div className="label-caps mb-2">Ciro Detayı</div>
          <table className="w-full text-sm mt-4">
            <thead><tr className="text-neutral-500 label-caps border-b border-white/5"><th className="text-left py-3">Ürün</th><th className="text-right py-3">Adet</th><th className="text-right py-3">Ciro</th></tr></thead>
            <tbody>
              {data.top_products.slice(0,10).map(p => (
                <tr key={p.product_id} className="border-b border-white/5">
                  <td className="py-3">{p.name}</td>
                  <td className="py-3 text-right font-mono">{p.quantity}</td>
                  <td className="py-3 text-right font-mono tabular-nums" style={{color:"var(--gold)"}}>{fmt2(p.revenue)}</td>
                </tr>
              ))}
              {data.top_products.length === 0 && <tr><td colSpan={3} className="text-center py-6 text-neutral-500">Veri yok</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
